import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ICompany, IDivision } from "@zonar-ui/auth/lib/models/company.model";
import { IPolicy, IUserGroupPolicy } from "@zonar-ui/auth/lib/models/user-group-policy.model";
import { SettingInfo } from "@zonar-ui/sidenav/lib/models/setting.model";
import { forkJoin, from, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { IGroup } from "../models/IGroup";
import { Config } from "../models/config.model";
import { Application, Asset } from "../models/core-api.models";
import { Category, ZoneApiParams, ZoneEntity } from "../models/inspection.model";
import { Inspector } from "../models/inspector.model";
import { InspectionTypeSummary } from "../models/inspection-type-summary.model";
import { LanguageDictionary } from "../models/language-dictionary.model";
import { BQInspection } from "../models/openAPIAliases";
import { ZoneLayoutSummary } from "../models/zone-layout-summary.model";
import { EvirDivision } from "../models/shared.model";
import { LoggerService } from "./logger.service";
import { RequestService } from "./request/request.service";
import { ServiceUtilsService } from "./service-utils.service";

@Injectable({
	providedIn: "root",
})
export class GlobalApiCallsService {
	constructor(
		private requestService: RequestService,
		public logger: LoggerService,
		public serviceUtils: ServiceUtilsService,
	) {}
	public getZones(companyId: string, endpoint: string): Observable<Zone[]> {
		return this.requestService.get<Zone[]>({
			url: `${endpoint}/zones?companyId=${encodeURIComponent(companyId)}`,
		});
	}
	public getCompany(companyId: string, endpoint: string): Observable<ICompany> {
		return this.requestService.get<ICompany>({
			url: `${endpoint}/companies/${encodeURIComponent(companyId)}`,
		});
	}

	public getCompanyTCUDevices(companyId: string, endpoint: string): Observable<HttpResponse<unknown>> {
		return this.requestService.get<HttpResponse<unknown>>({
			url: `${endpoint}/devices?companyId=${encodeURIComponent(companyId)}&deviceType=TCU`,
			httpOptions: { observe: "response" },
		});
	}

	public getApplication(applicationId: string, endpoint: string): Observable<Application> {
		return this.requestService.get<Application>({
			url: `${endpoint}/applications/${encodeURIComponent(applicationId)}`,
		});
	}

	public getInspectionTypes(companyId: string, endpoint: string): Observable<InspectionTypeSummary[]> {
		return this.requestService.get<InspectionTypeSummary[]>({
			url: `${endpoint}/inspection-types?companyId=${encodeURIComponent(companyId)}`,
		});
	}

	public getAssetTypes(companyId: string, endpoint: string): Observable<ZoneLayoutSummary[]> {
		return this.requestService.get<ZoneLayoutSummary[]>({
			url: `${endpoint}/zone-layouts?companyId=${encodeURIComponent(companyId)}`,
		});
	}

	public searchAssets(name: string, companyId: string): Promise<Asset[]> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/assets?companyId=${companyId}&status=ACTIVE&q=name:${name}`;
		const perPage = 1000;

		return this.requestService
			.head({ url, httpOptions: { observe: "response" } })
			.pipe(
				switchMap((response: HttpResponse<Object>) => {
					const totalAssets = Number(response.headers.get("x-total-count"));

					if (totalAssets === 0) {
						return of([]);
					}

					const totalPages = Math.ceil(totalAssets / perPage);
					const requests = Array.from({ length: totalPages }, (_, index) =>
						this.requestService.get({ url: `${url}&page=${index + 1}&per_page=${perPage}` }),
					);

					return forkJoin(requests);
				}),
				map((result: Asset[][]) => result.flat()),
			)
			.toPromise();
	}
	public getAssets(
		companyId: string,
		endpoint: string,
		hasAllDivisions: boolean,
		divisionIds: string[],
	): Observable<Asset[]> {
		if (hasAllDivisions) {
			return this.getAllAssetsForCompanyOrDivision(endpoint, companyId);
		}

		if (!(divisionIds && divisionIds.length)) {
			return of([]);
		}

		const requests = divisionIds.map(divisionId =>
			this.getAllAssetsForCompanyOrDivision(endpoint, companyId, divisionId),
		);
		return forkJoin(requests).pipe(
			map(results => Array.from(new Map(results.flat().map((item: Asset) => [item.id, item])).values())),
		);
	}

	public getInspectors(companyId: string, endpoint: string) {
		const uri = `${endpoint}/inspectors?companyId=${companyId}`;
		return this.serviceUtils.getAllPages(uri, "inspectors") as Observable<Inspector[]>;
	}
	public async searchInspectors(pattern: string, companyId: string): Promise<Inspector[]> {
		const uri = `${
			environment.environmentConstants.APP_ENDPOINT_EVIR_V2
		}/inspectors?companyId=${companyId}&pattern=${encodeURIComponent(pattern)}`;

		return (this.serviceUtils.getAllPages(uri, "inspectors") as Observable<Inspector[]>).toPromise();
	}
public getDivisionsLoader({
		endpointCompanies,
		divisionIds,
		hasAllDivisions,
		companyId,
	}: {
		divisionIds: ReadonlyArray<string>;
		hasAllDivisions: boolean;
		endpointCompanies: string;
		companyId: string;
	}) {
		return this.getCompanyDivision(companyId, endpointCompanies).pipe(
			map((divisions: Array<IDivision>) => {
				const dictionary = Object.fromEntries(divisions.map(({ id, ...data }) => [id, { id, ...data }]));

				return (hasAllDivisions
					? divisions
					: [
							...new Set(
								divisionIds.flatMap(id => [
									dictionary[id],
									...dictionary[id].children.map(childId => dictionary[childId]),
								]),
							),
					  ].filter(Boolean)
				).map(division => ({
					divisionId: division.id,
					divisionName: division.name,
					divisionType: division.type,
					parentDivision: dictionary[division.parentId],
					active: division.status.toLocaleLowerCase() === "active",
				})) as unknown as EvirDivision[];
			}),
		) as unknown as Observable<EvirDivision[]>;
	}

	public getDivision(companyId: string, divisionId: string, endpoint: string) {
		return this.requestService.get<IDivision>({
			url: `${endpoint}/companies/${encodeURIComponent(companyId)}/divisions/${encodeURIComponent(divisionId)}`,
		});
	}

	public getCompanyDivision(companyId: string, endpoint: string): Observable<Array<IDivision>> {
		return this.serviceUtils.getAllPages(
			`${endpoint}/companies/${encodeURIComponent(companyId)}/divisions?`,
			"company divisions",
			1000,
			1,
			"per_page",
		);
	}
	public searchCompanyDivision(name: string, companyId: string): Promise<Array<IDivision>> {
		return this.serviceUtils
			.getAllPages(
				`${environment.environmentConstants.APP_ENDPOINT_CORE}/companies/${encodeURIComponent(
					companyId,
				)}/divisions?q=name:${name}`,
				"company divisions",
				1000,
				1,
				"per_page",
			)
			.toPromise();
	}
	// this is singular because in app.state.ts, we're taking the first in the array
	public getConfig(companyId: string, endpoint: string): Observable<Config[]> {
		return this.requestService.get<Config[]>({
			url: `${endpoint}/configs?companyId=${encodeURIComponent(companyId)}&active=true`,
		});
	}

	public getPoliciesFromCompany(companyId: string, endpoint: string): Observable<Array<IPolicy>> {
		return this.requestService.get<Array<IPolicy>>({
			url: `${endpoint}/policies?companyId=${encodeURIComponent(companyId)}`,
		});
	}

	public getGroupsFromPolicy(policyId: string, endpoint: string): Observable<Array<IGroup>> {
		return this.requestService.get<Array<IGroup>>({
			url: `${endpoint}/groups?policyId=${encodeURIComponent(policyId)}`,
		});
	}

	public getLanguageDictionary(companyId: string, endpoint: string): Observable<LanguageDictionary> {
		const url = `${endpoint}/lang-dict?companyId=${companyId}`;

		return this.requestService.get<LanguageDictionary>({ url });
	}

	/**
	 * Get legacy account code from PX account ID
	 * @param pxAccountId PX Account ID
	 * @param endpoint Endpoint to make the call
	 * @returns Observable of mapping array
	 */
	public getLegacyAccountCode(pxAccountId: string, endpoint: string): Observable<Array<{ gtcAccountCode: string }>> {
		return this.requestService.get<Array<{ gtcAccountCode: string }>>({
			url: `${endpoint}/pxgtcmappings?pxEntityType=company&pxAccountId=${encodeURIComponent(
				pxAccountId,
			)}&per_page=5`,
		});
	}

	/**
	 * Get active GTC company
	 * @param legacyAccountCode Legacy account code. For example, "mbl2020"
	 * @param endpoint Endpoint to make the call
	 * @returns Observable of ICompany object for the legacy account code
	 */
	public getActiveGtcCompany(legacyAccountCode: string, endpoint: string): Observable<Array<ICompany>> {
		return this.requestService.get<Array<ICompany>>({
			url: `${endpoint}/companies?legacyAccountCode=${encodeURIComponent(legacyAccountCode)}`,
		});
	}

	/**
	 * Check if the company is using Group Policies
	 * @param companyId company ID
	 */
	public getCompanyLoginModeGroupPolicy(companyId: string): Observable<boolean> {
		const url = new URL(`${environment.environmentConstants.APP_ENDPOINT_CORE}/settings`);
		url.searchParams.set("ownerType", "COMPANY");
		url.searchParams.set("ownerId", companyId);
		url.searchParams.set("name", "GROUP_POLICY_STATUS");
		url.searchParams.set("status", "ACTIVE");
		return from(
			this.requestService
				.get<SettingInfo>({ url: url.toString() })
				.toPromise()
				.then(settings => {
					return settings[0]?.value === "ENABLED";
				}),
		);
	}

	public getEvirUserPolicies(userId: string) {
		const url = new URL(`${environment.environmentConstants.APP_ENDPOINT_CORE}/userpolicies`);
		url.searchParams.set("userId", userId);
		url.searchParams.set("applicationId", environment.environmentConstants.APP_APPLICATION_ID);
		return this.requestService.get<ReadonlyArray<IUserGroupPolicy>>({ url: url.toString() });
	}

	public getNJMVCInspectionData(
		companyId: string,
		{
			startDate,
			endDate,
			assetName,
			vehicleType,
		}: { startDate: Date; endDate: Date; assetName: string; vehicleType: string },
	) {
		const url = new URL(`${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/inspections/newjersey`);
		url.searchParams.set("companyId", companyId);
		url.searchParams.set("startTime", startDate.toISOString());
		url.searchParams.set("endTime", endDate.toISOString());
		url.searchParams.set("assetName", assetName);
		url.searchParams.set("vehicleType", vehicleType);
		return this.serviceUtils.getAllPages<Observable<ReadonlyArray<BQInspection>>>(
			url.toString(),
			"NJMVC Inspections",
		) as Observable<ReadonlyArray<BQInspection>>;
	}

	private getAllAssetsForCompanyOrDivision(
		endpoint: string,
		companyId: string,
		divisionId?: string,
	): Observable<Asset[]> {
		const url = divisionId
			? `${endpoint}/assets?companyId=${companyId}&divisionId=${divisionId}&allChildren=true&status=ACTIVE`
			: `${endpoint}/assets?companyId=${companyId}&status=ACTIVE`;
		const perPage = 1000;

		return this.requestService.head({ url, httpOptions: { observe: "response" } }).pipe(
			switchMap((response: HttpResponse<object>) => {
				const totalAssets = Number(response.headers.get("x-total-count"));

				if (totalAssets === 0) {
					return of([]);
				}

				const totalPages = Math.ceil(totalAssets / perPage);
				const requests = Array.from({ length: totalPages }, (_, index) =>
					this.requestService.get({ url: `${url}&page=${index + 1}&per_page=${perPage}` }),
				);

				return forkJoin(requests);
			}),
			map((result: Asset[][]) => result.flat()),
		);
	}

	public getZoneById(id: string): Observable<ZoneEntity> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/zones/simple?q=id:${id}`;
		return this.requestService.get({ url }, false).pipe(
			map(zone => {
				return zone[0];
			}),
		) as Observable<ZoneEntity>;
	}

	getCompanyZones(params?: ZoneApiParams): Observable<ZoneEntity[]> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/zones/simple`;
		return this.serviceUtils.getAll(url, params);
	}

	getCategoryById(categoryId: string): Observable<Category> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/categories/${categoryId}`;
		return this.requestService.get({ url });
	}

	getZoneCategories(companyId: string, search?: string): Observable<Category[]> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/categories`;
		const params = { companyId, sort: "name" };
		if (search) {
			(params as any).q = search;
		}
		return this.serviceUtils.getAll(url, params, 1000);
	}

	getCategoriesCount(companyId): Observable<number> {
		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/categories?companyId=${companyId}`;
		return this.requestService
			.head({
				url,
				httpOptions: { observe: "response" },
			})
			.pipe(
				map(response => {
					return parseInt((response as any).headers.get("X-Total-Count"));
				}),
			);
	}

	getZoneCount(params: ZoneApiParams): Observable<number> {
		const queryString = new URLSearchParams(params as any).toString();

		const url = `${environment.environmentConstants.APP_ENDPOINT_CORE}/zones?${queryString}`;
		return this.requestService
			.head({
				url,
				httpOptions: { observe: "response" },
			})
			.pipe(
				map(response => {
					return parseInt((response as any).headers.get("X-Total-Count"));
				}),
			);
	}
}
